import { useEffect, useState } from "react";
import type { Block } from "@/lib/api";

export interface CurrentSection {
  /** Heading text, e.g. "Typological Seriation". */
  title: string;
  /** Cumulative numbering — "2.2", "3.1.4" — built from heading level + sibling index. */
  number?: string;
}

/**
 * Track the topmost visible heading in the canvas — for the status whisper
 * section breadcrumb (LILIA-117, Pass 3 contract).
 *
 * Uses IntersectionObserver on every heading block's `data-block-id`
 * element; picks the heading whose top is closest to (but not below) a
 * threshold near the top of the viewport.
 *
 * Returns null when no heading is in view, or when there are no headings
 * in the doc — `StatusBar` hides the chip in that case.
 *
 * Numbering is computed by walking the blocks array up to the matched
 * heading: each level increments its counter, and a deeper level resets
 * any deeper counters. Mirrors LaTeX `\section`/`\subsection` numbering.
 */
export function useCurrentSection(blocks: Block[]): CurrentSection | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const headings = blocks.filter((b) => b.type === "heading");
    if (headings.length === 0) {
      setActiveId(null);
      return;
    }

    const elements: HTMLElement[] = [];
    for (const h of headings) {
      const el = document.querySelector<HTMLElement>(`[data-block-id="${h.id}"]`);
      if (el) elements.push(el);
    }
    if (elements.length === 0) return;

    // rootMargin: shift the bottom edge up so a heading is "active" once
    // it crosses ~25% from the top — feels stable while scrolling instead
    // of flipping on the very first pixel.
    const observer = new IntersectionObserver(
      () => {
        // On every change, recompute by reading bounding rects directly —
        // the entries-only path can miss the topmost-visible target when
        // multiple headings transition together.
        let pick: { id: string; top: number } | null = null;
        for (const el of elements) {
          const rect = el.getBoundingClientRect();
          // Heading is "topmost" if it's at or above 25% of viewport height.
          const threshold = window.innerHeight * 0.25;
          if (rect.top <= threshold) {
            if (!pick || rect.top > pick.top) {
              pick = { id: el.dataset.blockId!, top: rect.top };
            }
          }
        }
        // Fallback: nothing above threshold yet → use first visible heading.
        if (!pick) {
          for (const el of elements) {
            const rect = el.getBoundingClientRect();
            if (rect.bottom > 0 && rect.top < window.innerHeight) {
              pick = { id: el.dataset.blockId!, top: rect.top };
              break;
            }
          }
        }
        setActiveId(pick?.id ?? null);
      },
      { threshold: [0, 0.5, 1], rootMargin: "0px" }
    );

    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [blocks]);

  if (!activeId) return null;

  // Build the numbering by walking the blocks up to the active heading.
  const counters: number[] = []; // counters[level - 1]
  let pickedTitle = "";
  let pickedNumber: string | undefined;
  for (const b of blocks) {
    if (b.type !== "heading") continue;
    const level = Math.max(1, Math.min(6, (b.content?.level as number) || 1));
    // Increment this level's counter; reset deeper levels.
    counters[level - 1] = (counters[level - 1] ?? 0) + 1;
    for (let i = level; i < counters.length; i++) counters[i] = 0;
    if (b.id === activeId) {
      pickedTitle = ((b.content?.text as string) || "").trim() || "(untitled section)";
      pickedNumber = counters.slice(0, level).filter(Boolean).join(".");
      break;
    }
  }

  return pickedTitle ? { title: pickedTitle, number: pickedNumber || undefined } : null;
}
